import React from "react";
import * as LucideIcons from "lucide-react";
import { HelpCircle } from "lucide-react";

const ICON_MAP = {
  // Navigation & Core
  MagnifyingGlassIcon: "Search",
  PlusIcon: "Plus",
  XMarkIcon: "X",
  ChevronDownIcon: "ChevronDown",
  ChevronUpIcon: "ChevronUp",
  ChevronLeftIcon: "ChevronLeft",
  ChevronRightIcon: "ChevronRight",
  PencilSquareIcon: "Pencil",
  PencilIcon: "Edit3",
  ChatBubbleLeftRightIcon: "MessageSquare",
  ChatBubbleOvalLeftIcon: "MessageCircle",
  ChatBubbleBottomCenterIcon: "MessageSquare",

  // User & People
  UserIcon: "User",
  UserGroupIcon: "Users",
  LockClosedIcon: "Lock",
  LockOpenIcon: "LockOpen",
  UserPlusIcon: "UserPlus",
  UsersIcon: "Users",
  UserCircleIcon: "UserCircle",

  // Actions
  CheckIcon: "Check",
  TrashIcon: "Trash2",
  PencilIconAlt: "Edit",
  PaperAirplaneIcon: "Send",
  BoltIcon: "Zap",
  ArrowPathIcon: "RotateCcw",
  FunnelIcon: "Filter",

  // Files & Folders
  FolderIcon: "Folder",
  DocumentTextIcon: "FileText",
  DocumentIcon: "File",
  ArrowDownTrayIcon: "Download",
  ArrowUpTrayIcon: "Upload",

  // Time & Calendar
  ClockIcon: "Clock",
  CalendarIcon: "Calendar",
  CalendarDaysIcon: "CalendarDays",

  // Navigation arrows
  ArrowLeftIcon: "ArrowLeft",
  ArrowRightIcon: "ArrowRight",
  ArrowUpIcon: "ArrowUp",
  ArrowDownIcon: "ArrowDown",

  // Hashtag & Channel
  HashtagIcon: "Hash",

  // Notifications
  BellIcon: "Bell",
  BellSlashIcon: "BellOff",

  // Info & Help
  InformationCircleIcon: "Info",
  ExclamationTriangleIcon: "AlertTriangle",
  QuestionMarkCircleIcon: "HelpCircle",
  EyeIcon: "Eye",
  EyeSlashIcon: "EyeOff",

  // More options
  EllipsisVerticalIcon: "MoreVertical",
  EllipsisHorizontalIcon: "MoreHorizontal",

  // Pin
  PinIcon: "Pin",

  // Settings
  Cog6ToothIcon: "Settings",

  // Copy
  ClipboardIcon: "Clipboard",
  ClipboardDocumentIcon: "ClipboardList",

  // Star / Favorite
  StarIcon: "Star",
  StarOutline: "Star",

  // Archive
  ArchiveBoxIcon: "Archive",

  // Forward / Share
  ForwardIcon: "Forward",
  ShareIcon: "Share",

  // Reply
  ReplyIcon: "Reply",

  // Emoji & Expression
  FaceSmileIcon: "Smile",
  FaceFrownIcon: "Frown",
  MehIcon: "Meh",

  // Paperclip & Attachment
  PaperClipIcon: "Paperclip",

  // Microphone
  MicrophoneIcon: "Mic",
  MicrophoneSlashIcon: "MicOff",

  // Video
  VideoCameraIcon: "Video",
  VideoSlashIcon: "VideoOff",

  // Phone
  PhoneIcon: "Phone",
  PhoneSlashIcon: "PhoneOff",

  // Image / Photo
  PhotoIcon: "Image",

  // File types
  DocumentDuplicateIcon: "FileDuplicate",

  // Location
  MapPinIcon: "MapPin",

  // Link
  LinkIcon: "Link",
  ChainIcon: "Link",

  // Code
  CodeIcon: "Code2",
  CommandLineIcon: "Terminal",
  CodeBracketIcon: "Code2",

  // Bold / Italic
  BoldIcon: "Bold",
  ItalicIcon: "Italic",
  UnderlineIcon: "UnderlinedIcon",

  // Home
  HomeIcon: "Home",

  // Logout
  ArrowRightOnRectangleIcon: "LogOut",

  // Dots
  DotsVerticalIcon: "MoreVertical",
  DotsHorizontalIcon: "MoreHorizontal",

  // Music
  MusicalNoteIcon: "Music",
  PauseIcon: "Pause",
  PlayIcon: "Play",
  StopIcon: "Stop",

  // Table/Cells
  TableCellsIcon: "TableCells",
  TableIcon: "Table",

  // Mail
  EnvelopeIcon: "Mail",
  EnvelopeOpenIcon: "MailOpen",

  // Heart
  HeartIcon: "Heart",
  HeartOutline: "Heart",

  // Menu
  Bars3Icon: "Menu",
  MenuIcon: "Menu",

  // Lists
  ListBulletIcon: "List",
  ListOrderedIcon: "ListOrdered",
  ListChecksIcon: "ListTodo",

  // At Symbol / Mention
  AtSymbolIcon: "AtSign",

  // Panel / Layout
  PanelRightCloseIcon: "PanelRight",
  PanelRightOpenIcon: "PanelRight",
};

const ICON_SIZES = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  "2xl": 24,
  "3xl": 28,
  "4xl": 32,
  "5xl": 40,
  "6xl": 48,
};

const DEFAULT_SIZE = "lg";

function AppIcon({
  name,
  size = "lg",
  className = "",
  onClick,
  disabled = false,
  variant = "outline",
  strokeWidth = 1.5,
  ...props
}) {
  const mappedName = ICON_MAP[name] || name;
  const iconSize =
    typeof size === "number"
      ? size
      : ICON_SIZES[size] || ICON_SIZES[DEFAULT_SIZE];

  const IconComponent = LucideIcons[mappedName];

  if (!IconComponent) {
    return (
      <HelpCircle
        width={iconSize}
        height={iconSize}
        className={`text-gray-400 ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
        onClick={disabled ? undefined : onClick}
        {...props}
      />
    );
  }

  const iconProps = {
    width: iconSize,
    height: iconSize,
    strokeWidth: variant === "solid" ? undefined : strokeWidth,
    className: `
      ${disabled ? "opacity-50 cursor-not-allowed" : onClick ? "cursor-pointer transition-all duration-150 ease-out hover:opacity-70 active:scale-95" : ""}
      ${className}
    `.trim(),
    onClick: disabled ? undefined : onClick,
    ...props,
  };

  if (variant === "solid") {
    const SolidIcon =
      LucideIcons[`${mappedName}Solid`] || LucideIcons[`${mappedName}Fill`];
    if (SolidIcon) {
      return <SolidIcon {...iconProps} />;
    }
  }

  return <IconComponent {...iconProps} />;
}

export const IconSizes = ICON_SIZES;
export default AppIcon;
